import React from "react";
import { motion } from "framer-motion";
import {
  FaRegCalendarCheck,
  FaGraduationCap,
  FaPaperPlane,
} from "react-icons/fa6";

// Subtle dot-grid pattern used to fill whitespace in place of imagery
const DOT_PATTERN: React.CSSProperties = {
  backgroundImage: "radial-gradient(currentColor 1.5px, transparent 1.5px)",
  backgroundSize: "22px 22px",
};

const How = () => {
  return (
    <section className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-h2 font-light font-spartan tracking-tight text-gray-900 flex justify-center items-center gap-2">
            <span className="text-blue-00 italic">How</span>
            it works
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-auto lg:h-125!">
          {/* Step 1: Left Large Column */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-1 relative overflow-hidden rounded-4xl bg-[#EAF5FF] p-8 lg:p-10 flex flex-col justify-end border border-blue-100/50 group transition-all duration-300 hover:border-blue-200"
          >
            {/* Artifact: dot-grid pattern + ambient step icon (no imagery) */}
            <div
              aria-hidden
              className="absolute inset-0 z-0 text-blue-300/40 opacity-60 pointer-events-none"
              style={DOT_PATTERN}
            ></div>
            <FaRegCalendarCheck
              aria-hidden
              className="absolute top-1/4 -right-10 z-0 text-[220px] text-blue-200/30 pointer-events-none transition-transform duration-700 group-hover:scale-105"
            />

            {/* Content content */}
            <div className="relative z-10 w-full h-full flex flex-col justify-between">
              <p className="text-8xl font-spartan font-light text-blue-200/50 align-top self-end -mt-4 mr-2">
                1
              </p>
              <div>
                <h3 className="uppercase text-gray-900 mb-3 tracking-tight">
                  Book a Consultation
                </h3>
                <p className="text-gray-600 font-normal leading-relaxed text-base max-w-[95%]">
                  Schedule a session with our study abroad advisors to discuss
                  your academic goals and explore the best options for you.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Stacked */}
          <div className="lg:col-span-2 flex flex-col gap-6 h-full">
            {/* Step 2 */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex-1 relative overflow-hidden rounded-4xl bg-purple-50 p-6 lg:p-8 flex flex-col border border-purple-100/50 group transition-all duration-300 hover:border-purple-200"
            >
              {/* Artifact: dot-grid pattern + ambient step icon (no imagery) */}
              <div
                aria-hidden
                className="absolute inset-0 z-0 text-purple-300/40 opacity-50 pointer-events-none"
                style={DOT_PATTERN}
              ></div>
              <FaGraduationCap
                aria-hidden
                className="absolute -bottom-6 -right-4 z-0 text-[150px] text-purple-200/40 pointer-events-none transition-transform duration-700 group-hover:scale-105"
              />

              {/* Content Details */}
              <div className="relative z-10 flex flex-col h-full justify-between">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="uppercase text-gray-900 pr-4 mt-2 tracking-tight z-10">
                    Select Course & University
                  </h3>
                  <p className="text-6xl font-spartan font-light text-purple-200/60 shrink-0">
                    2
                  </p>
                </div>
                <p className="text-gray-600 font-normal leading-relaxed text-base max-w-[75%] z-10">
                  Get personalized recommendations based on your interests,
                  budget, and career aspirations.
                </p>
              </div>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex-1 relative overflow-hidden rounded-4xl bg-emerald-50 p-6 lg:p-8 flex flex-col border border-emerald-100/50 group transition-all duration-300 hover:border-emerald-200"
            >
              {/* Artifact: dot-grid pattern + ambient step icon (no imagery) */}
              <div
                aria-hidden
                className="absolute inset-0 z-0 text-emerald-300/40 opacity-50 pointer-events-none"
                style={DOT_PATTERN}
              ></div>
              <FaPaperPlane
                aria-hidden
                className="absolute -bottom-4 -right-2 z-0 text-[130px] text-emerald-200/40 -rotate-12 pointer-events-none transition-transform duration-700 group-hover:scale-105"
              />

              {/* Content Details */}
              <div className="relative z-10 flex flex-col h-full justify-between">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="uppercase text-gray-900 pr-4 mt-2 tracking-tight z-10">
                    Application & Submission
                  </h3>
                  <p className="text-6xl font-spartan font-light text-emerald-200/60 shrink-0">
                    3
                  </p>
                </div>
                <p className="text-gray-600 font-normal leading-relaxed text-base max-w-[75%] z-10">
                  Our team helps you prepare and submit a strong application to
                  maximize your chance at success.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default How;
